/**
 * programApi.js — Granullo Program Control API
 * Real, persistent program operations via backend.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

export async function createProgram(data) {
  const res = await fetch(`${API_URL}/api/program-control/programs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create program (${res.status})`);
  }
  return res.json();
}

export async function listPrograms(state = null, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (state) params.set('state', state);
  const res = await fetch(`${API_URL}/api/program-control/programs?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch programs (${res.status})`);
  return res.json();
}

export async function getProgram(programId) {
  const res = await fetch(`${API_URL}/api/program-control/programs/${programId}`);
  if (!res.ok) throw new Error(`Program not found (${res.status})`);
  return res.json();
}

export async function activateProgram(programId) {
  const res = await fetch(`${API_URL}/api/program-control/programs/${programId}/activate`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to activate (${res.status})`);
  }
  return res.json();
}

export async function completeCondition(programId, milestoneIndex, met = true, evidence = null) {
  const body = { milestone_index: milestoneIndex, met };
  if (evidence) body.evidence = evidence;
  const res = await fetch(`${API_URL}/api/program-control/programs/${programId}/conditions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update condition (${res.status})`);
  }
  return res.json();
}

export async function submitAssistedSetup(data) {
  const res = await fetch(`${API_URL}/api/assisted-setup/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to submit request (${res.status})`);
  }
  return res.json();
}

/* ══════════════════════════════════════════════════════════════
 * EXECUTION ENGINE API — Real Evaluation
 * Connects the homepage simulator to the real /evaluate endpoint.
 * Non-custodial. Fail-closed. Deterministic.
 * ══════════════════════════════════════════════════════════════ */

/**
 * Create a real execution program from parsed program data.
 * Returns { program_id, ... } or null on failure.
 */
export async function createExecutionProgram(parsed, rawText) {
  const amount = parseAmountToNumber(parsed.amount);
  const conditions = [];

  // Build conditions from parsed program
  if (parsed.allowedUsage && parsed.allowedUsage.length > 0 && parsed.allowedUsage[0] !== 'As specified') {
    conditions.push({
      type: 'merchant_category_match',
      required: true,
      description: `Allowed: ${parsed.allowedUsage.join(', ')}`,
      params: { allowed_categories: parsed.allowedUsage.map(u => u.toLowerCase().trim()) }
    });
  }

  if (amount > 0) {
    conditions.push({
      type: 'amount_limit',
      required: true,
      description: `Max ${parsed.amount}`,
      params: { max_amount: amount, min_amount: 0 }
    });
  }

  // Add conditions from parsed text
  if (parsed.conditions && parsed.conditions.length > 0) {
    parsed.conditions.forEach(c => {
      const cl = c.toLowerCase();
      if (cl.includes('delivery') || cl.includes('receipt')) {
        conditions.push({
          type: 'document_match',
          required: true,
          description: c,
          params: { required_documents: ['delivery_confirmation'] }
        });
      } else if (cl.includes('approv')) {
        conditions.push({
          type: 'multi_party_approval',
          required: true,
          description: c,
          params: { required_approvals: 1 }
        });
      } else if (cl.includes('verif') || cl.includes('identity')) {
        conditions.push({
          type: 'identity_verified',
          required: true,
          description: c,
        });
      } else {
        conditions.push({
          type: 'external_verification',
          required: true,
          description: c,
          params: { verification_key: c.toLowerCase().replace(/\s+/g, '_') }
        });
      }
    });
  }

  const payload = {
    name: parsed.type || 'Controlled Payment',
    description: rawText.substring(0, 500),
    sponsor_id: 'HOMEPAGE-SIMULATOR',
    participants: [],
    conditions,
    amount_constraints: {
      min_amount: 0,
      max_amount: amount || 1000000,
      currency: 'USD',
      per_transaction_limit: amount || 1000000,
    },
    time_constraints: {
      allowed_days: [0, 1, 2, 3, 4, 5, 6],
      allowed_hours_start: 0,
      allowed_hours_end: 24,
    },
    metadata: { source: 'homepage_simulator', program_type: parsed.type },
  };

  const res = await fetch(`${API_URL}/api/execution/programs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) return null;
  return res.json();
}

/**
 * Evaluate a transaction against a real execution program.
 * Returns the real evaluation result or null on failure.
 */
export async function evaluateExecution(programId, merchant) {
  const payload = {
    program_id: programId,
    transaction: {
      amount: merchant.amount || 0,
      currency: 'USD',
      merchant_id: merchant.name ? merchant.name.toLowerCase().replace(/\s+/g, '-') : 'unknown',
      merchant_category: merchant.category || merchant.name?.toLowerCase() || '',
      beneficiary_id: merchant.beneficiary || null,
      purpose: merchant.purpose || merchant.name?.toLowerCase() || null,
      identity_verified: merchant.allowed !== false,
      documents: merchant.documents || [],
      approvals: merchant.approvals || [],
      metadata: { source: 'homepage_simulator', merchant_allowed: merchant.allowed },
    },
  };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000); // 5s timeout

  try {
    const res = await fetch(`${API_URL}/api/execution/evaluate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    return res.json();
  } catch {
    clearTimeout(timeout);
    return null;
  }
}

/**
 * Map a real EvaluationResult to the UI format used by the simulator.
 * Preserves the existing UX contract.
 */
export function mapEvaluationToUI(evalResult, merchant) {
  if (!evalResult) return null;

  const checks = [];

  // Map passed conditions to green checks
  (evalResult.passed_conditions || []).forEach(condId => {
    checks.push({ text: condId.startsWith('CND-') ? 'Condition verified' : condId, pass: true });
  });

  // Map failed conditions to red checks
  (evalResult.failed_conditions || []).forEach(fc => {
    checks.push({ text: fc.reason || fc.condition_type, pass: false });
  });

  // Ensure at least one check for UX
  if (checks.length === 0) {
    checks.push({ text: evalResult.verdict === 'ALLOWED' ? 'All conditions satisfied' : 'Evaluation failed', pass: evalResult.verdict === 'ALLOWED' });
  }

  return {
    allowed: evalResult.verdict === 'ALLOWED',
    checks,
    reason: evalResult.failed_conditions?.[0]?.reason || '',
    merchant: merchant.name,
    amount: merchant.amount,
    programType: 'Evaluated',
    timestamp: Date.now(),
    proof_hash: evalResult.proof_hash,
    evaluation_id: evalResult.evaluation_id,
    latency_ms: evalResult.latency_ms,
    real: true, // Flag: this came from the real engine
  };
}

/* ── Helpers ── */

export function parseAmountToNumber(str) {
  if (!str || str === '\u2014') return 0;
  const cleaned = str.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
}

export function computeExpiryDate(timelineStr) {
  if (!timelineStr || timelineStr === 'No expiry defined') return null;
  const match = timelineStr.match(/(\d+)\s*(days?|months?|weeks?)/);
  if (!match) return null;
  const num = parseInt(match[1]);
  const unit = match[2].toLowerCase();
  const d = new Date();
  if (unit.startsWith('day')) d.setDate(d.getDate() + num);
  else if (unit.startsWith('week')) d.setDate(d.getDate() + num * 7);
  else if (unit.startsWith('month')) d.setMonth(d.getMonth() + num);
  return d.toISOString();
}

export function buildProgramPayload(parsed, rawText) {
  const amount = parseAmountToNumber(parsed.amount);
  const now = new Date().toISOString();
  const expiry = computeExpiryDate(parsed.timeline);

  const typeMap = {
    'Employee Benefit': { issuer: 'HR Department', beneficiary: 'Eligible Employee' },
    'Government Subsidy': { issuer: 'Government Agency', beneficiary: 'Registered Beneficiary' },
    'Insurance Payout': { issuer: 'Insurance Provider', beneficiary: 'Policyholder' },
    'Vendor Payment': { issuer: 'Contracting Organization', beneficiary: 'Verified Vendor' },
    'Grant Disbursement': { issuer: 'Grant Authority', beneficiary: 'Grant Recipient' },
    'Controlled Disbursement': { issuer: 'Program Issuer', beneficiary: 'Designated Beneficiary' },
  };

  const actors = typeMap[parsed.type] || { issuer: 'Issuing Organization', beneficiary: 'Designated Beneficiary' };
  const merchants = parsed.allowedUsage.filter(u => u !== 'As specified');
  const hasPendingConditions = parsed.conditions.length > 0 && parsed.conditions[0] !== 'Ready on creation';

  return {
    issuer: actors.issuer,
    beneficiary: actors.beneficiary,
    total_amount: amount || 1000,
    rules: {
      allowed_merchants: merchants,
      merchant_constraints: [parsed.type],
      geographic_constraints: {},
    },
    limits: {
      per_transaction_limit: amount || 1000,
      daily_limit: amount || 1000,
      monthly_limit: (amount || 1000) * 4,
    },
    timelines: {
      start_date: now,
      expiry_date: expiry,
      milestone_deadlines: [],
    },
    conditions: {
      milestones: hasPendingConditions
        ? parsed.conditions.map(c => ({ name: c, description: c, met: false, met_at: null, evidence: null }))
        : [{ name: 'Program rules defined', description: rawText.substring(0, 200), met: true, met_at: now, evidence: 'Created via Granullo' }],
      required_proofs: [],
      required_approvals: [],
    },
    unused_funds_logic: {
      refund_enabled: true,
      refund_to: actors.issuer,
      expiry_behavior: 'refund',
    },
  };
}

export function buildSandboxPayload(bp) {
  const amount = parseAmountToNumber(bp.amount);
  const now = new Date().toISOString();

  return {
    issuer: bp.sponsor || 'Program Issuer',
    beneficiary: bp.beneficiary || 'Designated Beneficiary',
    total_amount: amount || 1000,
    rules: {
      allowed_merchants: bp.participants || [],
      merchant_constraints: [bp.title || 'Program'],
      geographic_constraints: {},
    },
    limits: {
      per_transaction_limit: amount || 1000,
      daily_limit: amount || 1000,
      monthly_limit: (amount || 1000) * 4,
    },
    timelines: {
      start_date: now,
      expiry_date: null,
      milestone_deadlines: [],
    },
    conditions: {
      milestones: (bp.conditions || []).map(c => ({
        name: c,
        description: c,
        met: false,
        met_at: null,
        evidence: null,
      })),
      required_proofs: bp.proof ? [bp.proof] : [],
      required_approvals: bp.approval ? [bp.approval] : [],
    },
    unused_funds_logic: {
      refund_enabled: true,
      refund_to: bp.sponsor || 'Issuer',
      expiry_behavior: 'refund',
    },
  };
}
